import { Injectable, Renderer2, OnDestroy } from '@angular/core';
import { MatDatepickerInputEvent, MatDateRangePicker } from '@angular/material/datepicker';
import { DynamicFormReadyEvent, FilterSelectFieldConfig } from '@mydocma/dynamic-form';
import { UntypedFormGroup } from '@angular/forms';
import { MydocmaDatePipe } from '@mydocma/common';
import { DateFilterOptionsEnum } from './date-filter-options.enum';
import { dateFilterSource } from './date-filter-config';
import { MmFilterService } from '../mm-filter.service';
import { MmFaultsLoaderService } from '../../mm-faults/mm-faults-loader.service';
import { MmLayoutStateService } from '../layout/layout-state.service';
import { MmFilterModel } from '../mm-filter.model';
import { TranslateService } from '@ngx-translate/core';
import { FieldsUserFilterService } from './fields-user-filter.service';
import { FieldsAttributeFilterService } from './fields-attribute-filter.service';
import { FieldsCompanyCraftOrderService } from './fields-company-craft-order.service';
import { FieldsDelayFilterService } from './fields-delay-filter.service';
import { FieldsActivityStatusFilterService } from './fields-activity-status-filter.service';
import { FieldsResponsibleFilterService } from './fields-responsible-filter.service';
import { FieldsStatusGroupFilterService } from './fields-status-group-filter.service';
import { FieldsClassificationFilterService } from './fields-classification-filter.service';
import { FieldsProjectSpecificFilterService } from './fields-project-specific-filter.service';
import { firstValueFrom } from 'rxjs';
import { FieldMappingEnum } from './field-mapping.enum';
import { NotificationService } from '@mydocma/component-library/src/lib/notification';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EMPTY_FILTER_ID, EMPTY_FILTER_MARKER } from './field-mapping.enum';
import { AppContainerService } from '@mydocma/app-container';
import { Logger } from '@mydocma/common/src/lib/logger';

interface PendingFilterAction {
  fieldName: string;
  value?: string;
  displayValue?: string;
  isRemoval: boolean;
}

export interface FormValueChangeResult {
  config: any;
  value: any;
}

@Injectable()
export class FieldsFilterHandlerService implements OnDestroy {

  datePicker: MatDateRangePicker<any>;
  startDate: Date;
  endDate: Date;
  public filterFormData: DynamicFormReadyEvent;
  private renderer: Renderer2;
  private filterFormGroup: UntypedFormGroup;
  private currentSelectedDateFieldId: string;
  private initialState: any;
  private destroy$ = new Subject<void>();
  private pendingFilterActions: PendingFilterAction[] = [];
  private isMobileMode = false;

  constructor(
    private datePipe: MydocmaDatePipe,
    private mmFilterService: MmFilterService,
    private translateService: TranslateService,
    private userFilterService: FieldsUserFilterService,
    private attributeFilterService: FieldsAttributeFilterService,
    private companyCraftOrderService: FieldsCompanyCraftOrderService,
    private delayFilterService: FieldsDelayFilterService,
    private activityStatusFilterService: FieldsActivityStatusFilterService,
    private responsibleFilterService: FieldsResponsibleFilterService,
    private statusGroupFilterService: FieldsStatusGroupFilterService,
    private classificationFilterService: FieldsClassificationFilterService,
    private projectSpecificFilterService: FieldsProjectSpecificFilterService,
    private readonly notificationService: NotificationService,
    private appContainerService: AppContainerService,
    private readonly faultsLoaderService: MmFaultsLoaderService,
    private readonly layoutStateService: MmLayoutStateService,
    private readonly logger: Logger
  ) {
    this.appContainerService.mobileView$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isMobile => {
        const wasMobile = this.isMobileMode;
        this.isMobileMode = isMobile;

        if (wasMobile && !isMobile) {
          this.applyPendingFilters();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public attach(readyEvent: DynamicFormReadyEvent, renderer2: Renderer2): void {
    this.initService(readyEvent, renderer2);
    this.subscribeToFormEvents();
    this.subscribeToFilterServiceEvents();
    this.captureInitialState();
  }

  public onDatePickerChange(event: MatDatepickerInputEvent<Date>, type: 'start' | 'end'): void {
    this[type === 'start' ? 'startDate' : 'endDate'] = event.value;
    this.checkAndUpdateDates();
  }

  public datePickerReady(datePicker: MatDateRangePicker<Date>): void {
    this.datePicker = datePicker;
    this.datePicker?.closedStream
      .pipe(takeUntil(this.destroy$))
      .subscribe(val => {
        this.resetDateFields();
      });
  }

  private async showDatePicker(className: string): Promise<void> {
    if (!this.datePicker) {
      console.warn('Date picker not initialized yet');
      return;
    }

    const element = document.getElementsByClassName(className)[0];
    if (!element) return;

    const rect = element.getBoundingClientRect();
    const getInitialValue = this.initialState[this.currentSelectedDateFieldId];
    if (getInitialValue) {
      const dates = this.stringToDates(getInitialValue);
      this.endDate = dates.endDate;
      this.startDate = dates.startDate;
    }

    this.datePicker.open();

    await this.nextAnimationFrame();
    this.positionDatePicker(rect);
  }

  private captureInitialState(): void {
    this.initialState = JSON.parse(JSON.stringify(this.filterFormData.dynamicFormService.formGroup.value));
  }

  private initService(readyEvent: DynamicFormReadyEvent, renderer2: Renderer2): void {
    this.filterFormData = readyEvent;
    this.filterFormGroup = readyEvent.dynamicFormService.formGroup;
    this.renderer = renderer2;
  }

  private subscribeToFormEvents(): void {
    this.filterFormData.dynamicFormEventsService.onFieldValueChanged
      .pipe(takeUntil(this.destroy$))
      .subscribe((field: FormValueChangeResult) => {
        this.handleFieldValueChange(field);
      });
  }

  private subscribeToFilterServiceEvents(): void {
    this.mmFilterService.state
      .pipe(takeUntil(this.destroy$))
      .subscribe(filterState => {
        this.handleFilterStateChange(filterState);
      });
  }

  private handleFieldValueChange(field: FormValueChangeResult): void {
    const fieldName = field.config.id as string;

    if (field.value === EMPTY_FILTER_ID) {
      const displayValue = this.translateService.instant('MM.fields.filter.empty');
      this.applyOrStageFilter(fieldName, EMPTY_FILTER_MARKER, displayValue);
      this.initialState[fieldName] = this.filterFormGroup.get(fieldName).value;
      return;
    }

    if (fieldName === FieldMappingEnum.CreatedOn || fieldName === FieldMappingEnum.ModifiedOn) {
      this.handleDateFieldValueChange(field);
    } else if (fieldName === FieldMappingEnum.Creator || fieldName === FieldMappingEnum.Modifier) {
      this.handleUserFieldValueChange(field);
    } else if (fieldName === FieldMappingEnum.AttributeGroupImage || fieldName === FieldMappingEnum.AttributeName1) {
      this.handleAttributeFieldValueChange(field);
    } else if (fieldName === FieldMappingEnum.CompanyName1 || fieldName === FieldMappingEnum.CraftName1 || fieldName === FieldMappingEnum.OrderName1) {
      this.handleCompanyCraftOrderFieldValueChange(field);
    } else if (fieldName === FieldMappingEnum.DelayGroupSCId || fieldName === FieldMappingEnum.DelayGroupCTId) {
      this.handleDelayFieldValueChange(field);
    } else if (fieldName === FieldMappingEnum.LastActivitySCStatusName1 || fieldName === FieldMappingEnum.LastActivityCTStatusName1) {
      this.handleActivityStatusFieldValueChange(field);
    } else if (fieldName === FieldMappingEnum.ResponsibleCOCompany ||
               fieldName === FieldMappingEnum.ResponsibleSCCompany ||
               fieldName === FieldMappingEnum.ResponsibleCTCompany ||
               fieldName === FieldMappingEnum.ResponsibleCO ||
               fieldName === FieldMappingEnum.ResponsibleSC ||
               fieldName === FieldMappingEnum.ResponsibleCT) {
      this.handleResponsibleFieldValueChange(field);
    } else if (fieldName === FieldMappingEnum.LastActivitySCStatusGroupSC ||
               fieldName === FieldMappingEnum.LastActivityCOStatusGroupSC ||
               fieldName === FieldMappingEnum.LastActivityCOStatusGroupCT ||
               fieldName === FieldMappingEnum.LastActivityCTStatusGroupCT) {
      this.handleStatusGroupFieldValueChange(field);
    } else if (fieldName === FieldMappingEnum.Classification1Name1 ||
               fieldName === FieldMappingEnum.Classification2Name1 ||
               fieldName === FieldMappingEnum.Classification3Name1 ||
               fieldName === FieldMappingEnum.Classification4Name1) {
      this.handleClassificationFieldValueChange(field);
    } else if (fieldName === FieldMappingEnum.ProjectSpecificName1 ||
               fieldName === FieldMappingEnum.ProjectSpecific2Name1) {
      this.handleProjectSpecificFieldValueChange(field);
    }
  }

  private handleDateFieldValueChange(field: FormValueChangeResult): void {
    const fieldName = field.config.id as string;

    if (field.value === DateFilterOptionsEnum.customRange) {
      const prevValue = this.initialState[fieldName];
      setTimeout(() => this.filterFormData.dynamicFormService.updateFieldValue(fieldName, prevValue, false));
      this.currentSelectedDateFieldId = fieldName;
      this.showDatePicker(field.config.class);
    } else {
      if (this.initialState[fieldName] && !field?.value) {
        this.filterFormData.dynamicFormService.updateFieldConfig({
          name: fieldName,
          dropDownPanelClass: 'filter-date',
        } as FilterSelectFieldConfig);
        this.applyOrStageFilterRemoval(fieldName);
      } else if (field.value) {
        const displayValue = this.getDisplayValueForFilter(field.value);
        this.applyOrStageFilter(fieldName, field.value, displayValue);
      }
      this.initialState[fieldName] = this.filterFormGroup.get(fieldName).value;
    }
  }

  private async handleUserFieldValueChange(field: FormValueChangeResult): Promise<void> {
    const fieldName = field.config.id as string;
    const isRemove = !field.value;
    let displayValue: string;
    if (isRemove) {
      this.applyOrStageFilterRemoval(fieldName);
    } else {
      displayValue = await this.getUserDisplayValueForFilter(field.value);
    }
    if (!!displayValue || isRemove) {
      if (!isRemove) {
        this.applyOrStageFilter(fieldName, field.value, displayValue);
      }
      this.initialState[fieldName] = this.filterFormGroup.get(fieldName).value;
    } else {
      this.notificationService.error(this.translateService.instant('MM.fields.filter.errorUserFilter'));
      this.logger.error('[FieldsFilterHandlerService] No chip display value found for field: ' + fieldName, '', 'FieldsFilterHandlerService');
    }
  }

  private async getUserDisplayValueForFilter(userId: string): Promise<string> {
    try {
      return await firstValueFrom(this.userFilterService.getUserDisplayNameById(userId));
    } catch (error) {
      this.logger.error(error, '', 'FieldsFilterHandlerService');
      return;
    }
  }

  async handleAttributeFieldValueChange(field: FormValueChangeResult): Promise<void> {
    const fieldName = field.config.id as string;

    if (!field.value) {
      this.applyOrStageFilterRemoval(fieldName);
    } else {
      const displayValue = await this.getAttributeDisplayValueForFilter(fieldName, field.value);
      this.applyOrStageFilter(fieldName, field.value, displayValue);
    }
    this.initialState[fieldName] = this.filterFormGroup.get(fieldName).value;
  }

  private async getAttributeDisplayValueForFilter(fieldName: string, value: string): Promise<string> {
    try {
      if (fieldName === FieldMappingEnum.AttributeGroupImage) {
        return await firstValueFrom(this.attributeFilterService.getDisplayTextForAttributeGroup(value));
      } else if (fieldName === FieldMappingEnum.AttributeName1) {
        return await firstValueFrom(this.attributeFilterService.getDisplayTextForAttribute(value));
      }
      return value;
    } catch (error) {
      this.logger.error(error, '', 'FieldsFilterHandlerService');
      return value;
    }
  }

  private handleCompanyCraftOrderFieldValueChange(field: FormValueChangeResult): void {
    const fieldName = field.config.id as string;

    if (!field.value) {
      this.applyOrStageFilterRemoval(fieldName);
    } else {
      const displayValue = this.getCompanyCraftOrderDisplayValue(fieldName, field.value);
      this.applyOrStageFilter(fieldName, field.value, displayValue);
    }
    this.initialState[fieldName] = this.filterFormGroup.get(fieldName).value;
  }

  private getCompanyCraftOrderDisplayValue(fieldName: string, value: string): string {
    if (fieldName === FieldMappingEnum.CompanyName1) {
      return this.companyCraftOrderService.getCompanyDisplayNameById(value);
    } else if (fieldName === FieldMappingEnum.CraftName1) {
      return this.companyCraftOrderService.getCraftDisplayNameById(value);
    } else if (fieldName === FieldMappingEnum.OrderName1) {
      return this.companyCraftOrderService.getOrderDisplayNameById(value);
    }
    return value;
  }

  private nextAnimationFrame(): Promise<void> {
    return new Promise(resolve => requestAnimationFrame(() => resolve()));
  }

  private positionDatePicker(rect: ClientRect): void {
    const datePickerElement = document.querySelector('.mat-datepicker-content');
    if (!datePickerElement) return;

    const pickerRect = datePickerElement.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    this.renderer.setStyle(datePickerElement, 'position', 'absolute');
    this.renderer.setStyle(datePickerElement, 'left', `${rect.left}px`);

    const spaceBelow = windowHeight - rect.bottom;
    const spaceAbove = rect.top;
    const sufficientSpaceBelow = spaceBelow > pickerRect.height;
    const sufficientSpaceAbove = spaceAbove > pickerRect.height;

    if (sufficientSpaceBelow || !sufficientSpaceAbove) {
      this.renderer.setStyle(datePickerElement, 'top', `${rect.bottom - 7 + window.scrollY}px`);
    } else {
      this.renderer.setStyle(datePickerElement, 'bottom', `${windowHeight - rect.top + window.scrollY}px`);
    }
  }

  private checkAndUpdateDates(): void {
    if (this.startDate || this.endDate) {
      const startDate = this.startDate ? this.datePipe.transform(this.startDate, 'dd.MM.yyyy') : null;
      const endDate = this.endDate ? this.datePipe.transform(this.endDate, 'dd.MM.yyyy') : null;

      let parsedValue: string;
      if (this.startDate && this.endDate) {
        parsedValue = `${startDate} | ${endDate}`;
      } else if (this.startDate) {
        parsedValue = startDate;
      } else if (this.endDate) {
        parsedValue = endDate;
      }

      if (parsedValue) {
        this.updateDateTypeFormConfig(parsedValue);
        this.applyOrStageFilter(this.currentSelectedDateFieldId, parsedValue, parsedValue);
        this.initialState[this.currentSelectedDateFieldId] = parsedValue;
      }
    }
  }

  private resetDateFields(): void {
    this.currentSelectedDateFieldId = null;
    this.startDate = null;
    this.endDate = null;
  }

  private updateDateTypeFormConfig(parsedValue: string): void {
    this.filterFormData.dynamicFormService.updateFieldConfig({
      name: this.currentSelectedDateFieldId,
      dropDownPanelClass: 'custom-selected',
      source: [...dateFilterSource, {id: parsedValue, text: parsedValue}],
    } as FilterSelectFieldConfig);
    this.filterFormData.dynamicFormService.formGroup.get(this.currentSelectedDateFieldId)
      .setValue(parsedValue, {emitEvent: false});
  }

  private stringToDates(value: string): { startDate: Date; endDate: Date } {
    if (!value || !value.includes('|')) {
      return { startDate: null, endDate: null };
    }

    const [startStr, endStr] = value.split('|').map(s => s.trim());
    const parseDate = (str: string): Date => {
      const [day, month, year] = str.split('.').map(n => parseInt(n, 10));
      return new Date(year, month - 1, day);
    };

    return {
      startDate: parseDate(startStr),
      endDate: parseDate(endStr)
    };
  }

  private getDisplayValueForFilter(value: string): string {
    if (value && value.includes('|')) {
      return value;
    }

    const dateOption = dateFilterSource.find(option => option.id === value);
    return dateOption ? this.translateService.instant(dateOption.text) : value;
  }

  private handleFilterStateChange(filterState: MmFilterModel): void {
    if (!filterState.fieldFilters || !this.filterFormGroup) {
      return;
    }

    const formFieldNames = [
      FieldMappingEnum.CreatedOn,
      FieldMappingEnum.ModifiedOn,
      FieldMappingEnum.Creator,
      FieldMappingEnum.Modifier,
      FieldMappingEnum.AttributeGroupImage,
      FieldMappingEnum.AttributeName1,
      FieldMappingEnum.CompanyName1,
      FieldMappingEnum.CraftName1,
      FieldMappingEnum.OrderName1,
      FieldMappingEnum.ResponsibleCOCompany,
      FieldMappingEnum.ResponsibleCO,
      FieldMappingEnum.ResponsibleSCCompany,
      FieldMappingEnum.ResponsibleSC,
      FieldMappingEnum.ResponsibleCTCompany,
      FieldMappingEnum.ResponsibleCT,
      FieldMappingEnum.LastActivitySCStatusGroupSC,
      FieldMappingEnum.LastActivityCOStatusGroupSC,
      FieldMappingEnum.LastActivityCOStatusGroupCT,
      FieldMappingEnum.LastActivityCTStatusGroupCT,
      FieldMappingEnum.LastActivitySCStatusName1,
      FieldMappingEnum.LastActivityCTStatusName1,
      FieldMappingEnum.DelayGroupSCId,
      FieldMappingEnum.DelayGroupCTId,
      FieldMappingEnum.Classification1Name1,
      FieldMappingEnum.Classification2Name1,
      FieldMappingEnum.Classification3Name1,
      FieldMappingEnum.Classification4Name1,
      FieldMappingEnum.ProjectSpecificName1,
      FieldMappingEnum.ProjectSpecific2Name1
    ];

    formFieldNames.forEach(fieldName => {
      const currentFormValue = this.filterFormGroup.get(fieldName)?.value;
      const filterExists = filterState.fieldFilters.some(f => f.fieldName === fieldName);
      const hasPendingAction = this.isMobileMode && this.pendingFilterActions.some(action => action.fieldName === fieldName);

      if (currentFormValue && !filterExists && !hasPendingAction) {
        this.clearFormField(fieldName);
      }
    });
  }

  private clearFormField(fieldName: string): void {
    if (this.filterFormGroup && this.filterFormData) {
      this.filterFormGroup.get(fieldName)?.setValue(null, { emitEvent: false });

      if (fieldName === FieldMappingEnum.CreatedOn || fieldName === FieldMappingEnum.ModifiedOn) {
        this.filterFormData.dynamicFormService.updateFieldConfig({
          name: fieldName,
          dropDownPanelClass: 'filter-date',
          source: dateFilterSource
        } as FilterSelectFieldConfig);
      } else if (fieldName === FieldMappingEnum.Creator || fieldName === FieldMappingEnum.Modifier) {
        this.filterFormData.dynamicFormService.updateFieldConfig({
          name: fieldName,
          dropDownPanelClass: 'filter-user'
        } as FilterSelectFieldConfig);
      }

      this.initialState[fieldName] = null;
    }
  }

  private handleDelayFieldValueChange(field: FormValueChangeResult): void {
    const fieldName = field.config.id as string;
    const value = field.value as string;

    if (value && value !== '') {
      const displayValue = this.delayFilterService.getDelayDisplayNameById(value);
      this.applyOrStageFilter(fieldName, value, displayValue);
    } else {
      this.applyOrStageFilterRemoval(fieldName);
    }
    this.initialState[fieldName] = this.filterFormGroup.get(fieldName).value;
  }

  private handleActivityStatusFieldValueChange(field: FormValueChangeResult): void {
    const fieldName = field.config.id as string;
    const value = field.value as string;

    if (value && value !== '') {
      let displayValue: string;

      if (fieldName === FieldMappingEnum.LastActivitySCStatusName1) {
        displayValue = this.activityStatusFilterService.getSCStatusDisplayNameById(value);
      } else {
        displayValue = this.activityStatusFilterService.getCTStatusDisplayNameById(value);
      }

      this.applyOrStageFilter(fieldName, value, displayValue);
    } else {
      this.applyOrStageFilterRemoval(fieldName);
    }
    this.initialState[fieldName] = this.filterFormGroup.get(fieldName).value;
  }

  private handleResponsibleFieldValueChange(field: FormValueChangeResult): void {
    const fieldName = field.config.id as string;
    const value = field.value as string;

    if (value && value !== '') {
      let displayValue: string;

      if (fieldName.includes('Company')) {
        displayValue = this.responsibleFilterService.getCompanyDisplayNameById(value);
      } else {
        displayValue = this.responsibleFilterService.getContactDisplayNameById(value);
      }

      this.applyOrStageFilter(fieldName, value, displayValue);
    } else {
      this.applyOrStageFilterRemoval(fieldName);
    }
    this.initialState[fieldName] = this.filterFormGroup.get(fieldName).value;
  }

  private handleStatusGroupFieldValueChange(field: FormValueChangeResult): void {
    const fieldName = field.config.id as string;
    const value = field.value as string;

    if (value === null || value === undefined || value === '') {
      this.applyOrStageFilterRemoval(fieldName);
    } else {
      const displayValue = this.statusGroupFilterService.getStatusGroupDisplayNameById(value);
      this.applyOrStageFilter(fieldName, value, displayValue);
    }
    this.initialState[fieldName] = this.filterFormGroup.get(fieldName).value;
  }

  private handleClassificationFieldValueChange(field: FormValueChangeResult): void {
    const fieldName = field.config.id as string;
    const value = field.value as string;

    if (!value) {
      this.applyOrStageFilterRemoval(fieldName);
    } else {
      const displayValue = this.getClassificationDisplayValue(fieldName, value);
      this.applyOrStageFilter(fieldName, value, displayValue);
    }
    this.initialState[fieldName] = this.filterFormGroup.get(fieldName).value;
  }

  private getClassificationDisplayValue(fieldName: string, value: string): string {
    switch (fieldName) {
      case FieldMappingEnum.Classification1Name1:
        return this.classificationFilterService.getClassification1DisplayNameById(value);
      case FieldMappingEnum.Classification2Name1:
        return this.classificationFilterService.getClassification2DisplayNameById(value);
      case FieldMappingEnum.Classification3Name1:
        return this.classificationFilterService.getClassification3DisplayNameById(value);
      case FieldMappingEnum.Classification4Name1:
        return this.classificationFilterService.getClassification4DisplayNameById(value);
      default:
        this.logger.error('[getClassificationDisplayValue] Unknown classification field: ' + fieldName, '', 'FieldsFilterHandlerService');
        return value;
    }
  }

  private handleProjectSpecificFieldValueChange(field: FormValueChangeResult): void {
    const fieldName = field.config.id as string;
    const value = field.value as string;

    if (!value || value === '') {
      this.applyOrStageFilterRemoval(fieldName);
    } else {
      const displayValue = this.getProjectSpecificDisplayValue(fieldName, value);
      this.applyOrStageFilter(fieldName, value, displayValue);
    }
    this.initialState[fieldName] = this.filterFormGroup.get(fieldName).value;
  }

  private getProjectSpecificDisplayValue(fieldName: string, value: string): string {
    switch (fieldName) {
      case FieldMappingEnum.ProjectSpecificName1:
        return this.projectSpecificFilterService.getProjectSpecific1DisplayNameById(value);
      case FieldMappingEnum.ProjectSpecific2Name1:
        return this.projectSpecificFilterService.getProjectSpecific2DisplayNameById(value);
      default:
        this.logger.error('[getProjectSpecificDisplayValue] Unknown project specific field: ' + fieldName, '', 'FieldsFilterHandlerService');
        return value;
    }
  }

  private applyOrStageFilter(fieldName: string, value: string, displayValue: string): void {
    if (this.isMobileMode) {
      this.stageFilterAction(fieldName, value, displayValue, false);
    } else {
      this.mmFilterService.addFieldFilter(fieldName, value, displayValue);
    }
  }

  private applyOrStageFilterRemoval(fieldName: string): void {
    if (this.isMobileMode) {
      this.stageFilterAction(fieldName, '', '', true);
    } else {
      this.mmFilterService.removeFieldFilter(fieldName);
    }
  }

  private stageFilterAction(fieldName: string, value: string, displayValue: string, isRemoval: boolean): void {
    const existingIndex = this.pendingFilterActions.findIndex(action => action.fieldName === fieldName);
    const action: PendingFilterAction = {
      fieldName,
      value,
      displayValue,
      isRemoval
    };

    if (existingIndex >= 0) {
      this.pendingFilterActions[existingIndex] = action;
    } else {
      this.pendingFilterActions.push(action);
    }
  }

  public applyPendingFilters(): void {
    this.pendingFilterActions.forEach(action => {
      if (action.isRemoval) {
        this.mmFilterService.removeFieldFilter(action.fieldName);
      } else {
        this.mmFilterService.addFieldFilter(action.fieldName, action.value, action.displayValue);
      }
    });
    this.pendingFilterActions = [];
  }

  public clearPendingFilters(): void {
    this.pendingFilterActions.forEach(action => {
      if (action.isRemoval) {
        this.clearFormField(action.fieldName);
      }
    });

    this.pendingFilterActions = [];
  }
}
